import React, { useState, useEffect } from 'react';
import { ContactCollection, ContactCollectionWithCount, CollectionShareWithDetails } from '../types/Collections';
import { Contact } from '../types/Contact';
import { supabase } from '../lib/supabase';
import { deleteCollectionCore, listCollectionsWithSharingCore, listMembershipsForCollectionsCore, getReceivedSharesCore, updateShareStatusCore, getCollectionCore } from '@gv/core';
import { useNotifications } from '../hooks/useNotifications';
import { User } from '@supabase/supabase-js';
import {
    Plus,
    Edit2,
    Trash2,
    Users,
    Search,
    FolderPlus,
    Folder,
    MoreVertical,
    UserPlus,
    Upload,
    Eye,
    Calendar,
    Share
} from 'lucide-react';
import CollectionEditorPage from './CollectionEditorPage';
import BulkContactAdder from './BulkContactAdder';
import ShareCollectionModal from './ShareCollectionModal';

interface ContactCollectionsProps {
    user: User;
    onContactSelect?: (contact: Contact, collectionContext?: string, collectionName?: string) => void;
    onCollectionSelect?: (collectionId: string) => void;
    onCreateNew?: () => void; // navigate to full-page editor
}

export default function ContactCollections({ user, onContactSelect, onCollectionSelect, onCreateNew }: ContactCollectionsProps) {
    const [collections, setCollections] = useState<ContactCollectionWithCount[]>([]);
    const [pendingInvites, setPendingInvites] = useState<CollectionShareWithDetails[]>([]);
    const [loading, setLoading] = useState(true);
    const { notification, showNotification, hideNotification } = useNotifications ? useNotifications() : { notification: null, showNotification: () => {}, hideNotification: () => {} };
    const [selectedCollection, setSelectedCollection] = useState<ContactCollection | null>(null);
    const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
    const [isBulkAdderOpen, setIsBulkAdderOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<'name' | 'created' | 'updated' | 'count'>('created');
    const [showSearch, setShowSearch] = useState(false);

    useEffect(() => {
        fetchCollections();
            fetchInvitations();
        const listener = () => fetchCollections();
        window.addEventListener('gv:refreshCollections' as any, listener as any);
        return () => window.removeEventListener('gv:refreshCollections' as any, listener as any);
    }, [user]);
    const fetchInvitations = async () => {
        try {
            const result = await getReceivedSharesCore(supabase);
            if (result.success) {
                const invites: CollectionShareWithDetails[] = (result.data || []).filter((inv: CollectionShareWithDetails) => inv.status === 'pending');

                // Enrich invites that are missing collection details
                const needsFetch = invites.filter(i => !i.collection || !i.collection.name);
                if (needsFetch.length > 0) {
                    await Promise.all(needsFetch.map(async inv => {
                        try {
                            const col = await getCollectionCore(supabase as any, inv.collection_id);
                            if (col) {
                                // Get member count for this collection
                                try {
                                    const members = await listMembershipsForCollectionsCore(supabase as any, [inv.collection_id]);
                                    (col as any).member_count = members ? members.length : 0;
                                } catch (e) {
                                    (col as any).member_count = 0;
                                }
                                inv.collection = col as any;
                            }
                        } catch (e) {
                            // ignore
                        }
                    }));
                }

                setPendingInvites(invites);
            } else {
                showNotification('Failed to fetch collection invitations', 'error');
            }
        } catch (error) {
            console.error('Error fetching invitations:', error);
            showNotification('Failed to fetch collection invitations', 'error');
        }
    };
    const handleInvitationResponse = async (shareId: string, status: 'accepted' | 'declined') => {
        try {
            const result = await updateShareStatusCore(supabase, shareId, status);
            if (!result.success) {
                showNotification(`Failed to ${status === 'accepted' ? 'accept' : 'decline'} invitation`, 'error');
                return;
            }
            setPendingInvites(prev => prev.filter(inv => inv.id !== shareId));
            fetchCollections();
            showNotification(`Collection invitation ${status === 'accepted' ? 'accepted' : 'declined'}`, 'success');
        } catch (error) {
            console.error('Error responding to invitation:', error);
            showNotification('Failed to process invitation', 'error');
        }
    };

    // For sender: cancel invite
    const handleCancelInvite = async (shareId: string) => {
        try {
            const result = await updateShareStatusCore(supabase, shareId, 'declined');
            if (!result.success) {
                showNotification('Failed to cancel invitation', 'error');
                return;
            }
            setPendingInvites(prev => prev.filter(inv => inv.id !== shareId));
            showNotification('Invitation cancelled', 'success');
        } catch (error) {
            console.error('Error cancelling invitation:', error);
            showNotification('Failed to cancel invitation', 'error');
        }
    };

    const fetchCollections = async () => {
        setLoading(true);
        try {
            // Fetch collections via core (including shared collections)
            const cols = await listCollectionsWithSharingCore(supabase as any, user.id, user.email || '');
            // The new function already returns member_count, so we can use it directly
            const collectionsWithCount: ContactCollectionWithCount[] = (cols || []).map((c: any) => ({
                ...c,
                member_count: c.member_count || 0,
            }));

            // Sort by created_at desc to match original UX if available
            collectionsWithCount.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
            setCollections(collectionsWithCount);
        } catch (error) {
            console.error('Error fetching collections:', error);
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        if (onCreateNew) { onCreateNew(); return; }
        setSelectedCollection(null);
        setIsCollectionModalOpen(true);
    };

    const openEditModal = (collection: ContactCollection) => {
        setSelectedCollection(collection);
        setIsCollectionModalOpen(true);
    };

    const openViewModal = (collection: ContactCollection) => {
        if (onCollectionSelect) {
            onCollectionSelect(collection.id);
        }
    };

    const openBulkAdder = (collection?: ContactCollection) => {
        setSelectedCollection(collection || null);
        setIsBulkAdderOpen(true);
    };

    const openShareModal = (collection: ContactCollection) => {
        setSelectedCollection(collection);
        setIsShareModalOpen(true);
    };

    const handleCollectionSave = () => {
        fetchCollections();
        setIsCollectionModalOpen(false);
    };

    const handleCollectionDelete = async (collectionId: string) => {
        if (!window.confirm('Are you sure you want to delete this collection? This action cannot be undone.')) {
            return;
        }

        try {
            await deleteCollectionCore(supabase as any, collectionId);

            setCollections(prev => prev.filter(c => c.id !== collectionId));
        } catch (error) {
            console.error('Error deleting collection:', error);
            alert('Failed to delete collection');
        }
    };

    const filteredCollections = collections
        .filter(collection =>
            collection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (collection.description || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'count':
                    return b.member_count - a.member_count;
                case 'updated':
                    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
                case 'created':
                default:
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            }
        });

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-900 to-black border border-gray-800 p-6 lg:p-8 text-white">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div>
                        <h2 className="text-2xl lg:text-3xl font-light mb-2 tracking-wide">Contact Collections</h2>
                        <p className="text-gray-300 text-base lg:text-lg font-light">
                            Organize contacts into campaigns and custom groups
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowSearch(!showSearch)}
                            className={`inline-flex items-center justify-center px-4 py-2 border transition-colors font-medium text-sm tracking-wide ${showSearch
                                ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                                : 'bg-gray-800 text-white border-gray-700 hover:bg-gray-700'
                                }`}
                        >
                            <Search className="w-4 h-4 mr-2" />
                            SEARCH
                        </button>
                        <button
                            onClick={openCreateModal}
                            className="inline-flex items-center justify-center px-6 py-3 bg-white text-black hover:bg-gray-100 transition-colors font-semibold text-sm tracking-wide"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            NEW COLLECTION
                        </button>
                    </div>
                </div>

                {/* Search Bar - Conditionally shown */}
                {showSearch && (
                    <div className="mt-6 pt-6 border-t border-gray-700">
                        <div className="relative max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Collections Grid */}
            {(filteredCollections.length > 0 || pendingInvites.length > 0) ? (
                <div className="px-3 relative">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Pending Invitations */}
                        {pendingInvites.map((invitation) => (
                            <div
                                key={invitation.id}
                                className="bg-gradient-to-br from-yellow-900/40 to-gray-800 border border-yellow-700 hover:border-yellow-500 hover:shadow-xl transition-all rounded-lg relative opacity-100"
                            >
                                <div className="p-6 border-b border-gray-800 relative overflow-visible">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center space-x-3">
                                            <h3 className="text-lg font-medium text-yellow-200 truncate">{invitation.collection?.name || 'Shared Collection'}</h3>
                                            <span className="flex items-center gap-1 text-xs bg-yellow-900/30 text-yellow-400 px-2 py-1 rounded border border-yellow-700/30">
                                                <Users className="w-3 h-3" />
                                                Pending Invite
                                            </span>
                                        </div>
                                        {/* Cancel for sender, Accept/Decline for receiver */}
                                        <div className="relative z-20">
                                            {(invitation.shared_to_user_id === user.id || (invitation.shared_to_email || '').toLowerCase() === (user.email || '').toLowerCase()) ? null : (
                                                <button
                                                    onClick={() => handleCancelInvite(invitation.id)}
                                                    className="text-red-400 hover:text-white p-1 border border-red-700/30 rounded"
                                                >
                                                    Cancel Invite
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {invitation.collection?.description && (
                                        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{invitation.collection.description}</p>
                                    )}
                                    <div className="flex items-center justify-between text-sm">
                                        <div className="flex items-center text-gray-300">
                                            <Users className="w-4 h-4 mr-1" />
                                            <span>{invitation.collection?.member_count || 0} contacts</span>
                                        </div>
                                        <div className="flex items-center text-gray-400">
                                            <Calendar className="w-4 h-4 mr-1" />
                                            <span>{formatDate(invitation.created_at)}</span>
                                        </div>
                                    </div>
                                </div>
                                {/* Quick Actions for receiver */}
                                {(invitation.shared_to_user_id === user.id || (invitation.shared_to_email || '').toLowerCase() === (user.email || '').toLowerCase()) && (
                                    <div className="p-4 flex space-x-2">
                                        <button
                                            onClick={() => handleInvitationResponse(invitation.id, 'accepted')}
                                            className="flex-1 py-2 px-3 bg-blue-700 border border-blue-800 text-white text-sm hover:bg-blue-800 transition-colors rounded"
                                        >
                                            Accept
                                        </button>
                                        <button
                                            onClick={() => handleInvitationResponse(invitation.id, 'declined')}
                                            className="flex-1 py-2 px-3 bg-gray-900 border border-gray-700 text-red-400 text-sm hover:bg-gray-800 transition-colors rounded"
                                        >
                                            Decline
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                        {/* Normal Collections */}
                        {filteredCollections.map((collection) => (
                            <div
                                key={collection.id}
                                onClick={() => openViewModal(collection)}
                                className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 hover:border-gray-600 hover:shadow-xl transition-all cursor-pointer rounded-lg relative"
                            >
                                {/* ...existing code... */}
                                <div className="p-6 border-b border-gray-800 relative overflow-visible">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center space-x-3">
                                            <h3 className="text-lg font-medium text-white truncate">{collection.name}</h3>
                                            {(collection as any).is_shared && (
                                                <span className="flex items-center gap-1 text-xs bg-blue-900/30 text-blue-400 px-2 py-1 rounded border border-blue-700/30">
                                                    <Users className="w-3 h-3" />
                                                    Shared
                                                </span>
                                            )}
                                        </div>
                                        {/* Action Menu */}
                                        <div className="relative group z-20">
                                            <button
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-gray-400 hover:text-white p-1"
                                            >
                                                <MoreVertical className="w-4 h-4" />
                                            </button>
                                            {/* Dropdown Menu */}
                                            <div className="absolute right-0 top-full mt-2 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                                                style={{
                                                    position: 'absolute',
                                                    right: '0',
                                                    top: '100%',
                                                    marginTop: '8px',
                                                    minWidth: '192px',
                                                    zIndex: 1000,
                                                    overflow: 'visible'
                                                }}>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openViewModal(collection);
                                                    }}
                                                    className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-800 flex items-center first:rounded-t-lg"
                                                >
                                                    <Eye className="w-4 h-4 mr-2" />
                                                    View Members
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openBulkAdder(collection);
                                                    }}
                                                    className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-800 flex items-center"
                                                >
                                                    <UserPlus className="w-4 h-4 mr-2" />
                                                    Add Contacts
                                                </button>
                                                {/* Only show edit for owned collections */}
                                                {!(collection as any).is_shared && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openEditModal(collection);
                                                        }}
                                                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-800 flex items-center"
                                                    >
                                                        <Edit2 className="w-4 h-4 mr-2" />
                                                        Edit Collection
                                                    </button>
                                                )}
                                                {/* Only show share for owned collections */}
                                                {!(collection as any).is_shared && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openShareModal(collection);
                                                        }}
                                                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-800 flex items-center"
                                                    >
                                                        <Share className="w-4 h-4 mr-2" />
                                                        Share Collection
                                                    </button>
                                                )}
                                                {(!(collection as any).is_shared || (collection as any).is_shared) && (
                                                    <div className="border-t border-gray-700"></div>
                                                )}
                                                {/* Only show delete for owned collections */}
                                                {!(collection as any).is_shared ? (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCollectionDelete(collection.id);
                                                        }}
                                                        className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-800 hover:text-red-300 flex items-center rounded-b-lg"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Delete
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // TODO: Leave shared collection
                                                            console.log('Leave shared collection:', collection.id);
                                                        }}
                                                        className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-800 hover:text-red-300 flex items-center rounded-b-lg"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Leave Collection
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {collection.description && (
                                        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{collection.description}</p>
                                    )}
                                    <div className="flex items-center justify-between text-sm">
                                        <div className="flex items-center text-gray-300">
                                            <Users className="w-4 h-4 mr-1" />
                                            <span>{collection.member_count} contacts</span>
                                        </div>
                                        <div className="flex items-center text-gray-400">
                                            <Calendar className="w-4 h-4 mr-1" />
                                            <span>{formatDate(collection.created_at)}</span>
                                        </div>
                                    </div>
                                </div>
                                {/* Quick Actions */}
                                <div className="p-4 flex space-x-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openViewModal(collection);
                                        }}
                                        className="flex-1 py-2 px-3 bg-gray-900 border border-gray-700 text-white text-sm hover:bg-gray-800 transition-colors"
                                    >
                                        View
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openBulkAdder(collection);
                                        }}
                                        className="flex-1 py-2 px-3 bg-gray-900 border border-gray-700 text-white text-sm hover:bg-gray-800 transition-colors"
                                    >
                                        Add Contacts
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="text-center py-12 mx-3 bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg">
                    <FolderPlus className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-light text-white mb-2 tracking-wide">No collections found</h3>
                    <p className="text-gray-400 mb-6 font-light">
                        {searchTerm ? 'Try adjusting your search terms' : 'Create your first collection to organize contacts'}
                    </p>
                    {!searchTerm && (
                        <button
                            onClick={openCreateModal}
                            className="inline-flex items-center px-6 py-3 bg-white text-black hover:bg-gray-100 transition-colors font-semibold text-sm tracking-wide"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            CREATE COLLECTION
                        </button>
                    )}
                </div>
            )}
            {/* Retain overlay editor as fallback when onCreateNew isn't provided */}
            {isCollectionModalOpen && (
                <div className="fixed inset-0 z-50 bg-black">
                    <CollectionEditorPage
                        user={user}
                        collectionId={selectedCollection?.id || null}
                        onBack={() => setIsCollectionModalOpen(false)}
                        onSaved={() => {
                            handleCollectionSave();
                        }}
                    />
                </div>
            )}

            {isBulkAdderOpen && (
                <BulkContactAdder
                    targetCollection={selectedCollection}
                    user={user}
                    onClose={() => setIsBulkAdderOpen(false)}
                    onContactsAdded={fetchCollections}
                />
            )}

            {isShareModalOpen && selectedCollection && (
                <ShareCollectionModal
                    collection={selectedCollection}
                    user={user}
                    onClose={() => setIsShareModalOpen(false)}
                    onShared={() => {
                        setIsShareModalOpen(false);
                        fetchCollections(); // Refresh to show any new status
                    }}
                />
            )}
        </div>
    );
}
